/**
 * Cliente HTTP para los endpoints públicos del comensal (Menú QR).
 * No requiere sesión de usuario ni token Bearer.
 */
import { API_BASE, ErrorApi } from './api';

export interface InfoMesaPublica {
  token: string;
  sucursalId: number;
  sucursalNombre: string;
  mesaNumero: number;
  mesaNombre: string;
  estadoMesa: 'libre' | 'ocupada' | 'por_cobrar';
  cuentaActivaId: number | null;
  cuentaTotal: number;
}

export interface CategoriaPublica {
  id: number;
  nombre: string;
  orden: number;
}

export interface OpcionModificador {
  id: number;
  grupo_id: number;
  nombre: string;
  precio_extra: number | string;
  orden: number;
}

export interface GrupoModificador {
  id: number;
  nombre: string;
  seleccion: 'una' | 'varias';
  obligatorio: number;
  opciones: OpcionModificador[];
}

export interface ProductoPublico {
  id: number;
  categoria_id: number | null;
  nombre: string;
  descripcion: string | null;
  imagen_url: string | null;
  precio: number | string;
  por_peso: number;
  minutos_prep: number | null;
  modificadores?: GrupoModificador[];
}

export interface MenuPublicoRespuesta {
  sucursalId: number;
  mesaNumero: number;
  categorias: CategoriaPublica[];
  productos: ProductoPublico[];
  modificadores: GrupoModificador[];
}

export interface ItemCarrito {
  producto: ProductoPublico;
  cantidad: number;
  notas: string;
  opcionesModificadores: OpcionModificador[];
  subtotal: number;
}

export interface ItemOrdenEnvio {
  productoId: number;
  cantidad: number;
  notas?: string;
  modificadores?: { grupoId: number; opcionId: number }[];
}

export interface DetalleOrdenPublica {
  id: number;
  producto_id: number;
  producto_nombre: string;
  imagen_url: string | null;
  cantidad: number;
  precio_unitario: number | string;
  subtotal: number | string;
  notas: string | null;
  estado: string;
  modificadores: { grupo_id: number; opcion_id: number; nombre_opcion: string; precio_extra: number | string }[];
}

export interface OrdenPublica {
  id: number;
  numero_orden: number;
  estado: 'recibida' | 'en_preparacion' | 'lista' | 'entregada' | 'cancelada';
  total: number | string;
  notas: string | null;
  creado_en: string;
  detalles: DetalleOrdenPublica[];
}

export interface ResumenCuentaPublica {
  id?: number;
  mesa_numero?: number;
  mesa_nombre?: string;
  total?: number | string;
  ordenes: OrdenPublica[];
}

export interface InfoMostradorPublica {
  sucursalId: number;
  sucursalNombre: string;
}

export interface MenuMostradorRespuesta {
  sucursalId: number;
  categorias: CategoriaPublica[];
  productos: ProductoPublico[];
  modificadores: GrupoModificador[];
}

export interface DetalleOrdenMostrador {
  id: number;
  cantidad: number;
  subtotal: number | string;
  producto_nombre: string;
}

export interface OrdenMostrador {
  id: number;
  numero_orden: number;
  estado: 'recibida' | 'en_preparacion' | 'lista' | 'entregada' | 'cancelada';
  total: number | string;
  notas: string | null;
  nombre_cliente: string;
  creado_en: string;
  detalles: DetalleOrdenMostrador[];
}

export async function peticionPublica<T>(ruta: string, opciones: RequestInit = {}): Promise<T> {
  const control = new AbortController();
  const t = setTimeout(() => control.abort(), 12000);

  try {
    const res = await fetch(`${API_BASE}/api/publico${ruta}`, {
      ...opciones,
      signal: control.signal,
      headers: {
        'Content-Type': 'application/json',
        ...opciones.headers,
      },
    });

    const json = await res.json().catch(() => null);
    if (!res.ok || !json?.ok) {
      throw new ErrorApi(json?.mensaje ?? 'Error al procesar la solicitud.', json?.codigo ?? 'ERROR_PUBLICO', res.status);
    }
    return json.datos as T;
  } finally {
    clearTimeout(t);
  }
}

export const publicApi = {
  obtenerMesa: (token: string) => peticionPublica<InfoMesaPublica>(`/mesa/${token}`),
  obtenerMenu: (token: string) => peticionPublica<MenuPublicoRespuesta>(`/menu/${token}`),
  enviarOrden: (tokenMesa: string, items: ItemOrdenEnvio[], notas?: string) =>
    peticionPublica<OrdenPublica>('/ordenes', {
      method: 'POST',
      body: JSON.stringify({ tokenMesa, items, notas }),
    }),
  obtenerCuenta: (token: string) => peticionPublica<ResumenCuentaPublica>(`/cuenta/${token}`),

  obtenerMostrador: (token: string) => peticionPublica<InfoMostradorPublica>(`/mostrador/${token}`),
  obtenerMenuMostrador: (token: string) => peticionPublica<MenuMostradorRespuesta>(`/menu-mostrador/${token}`),
  enviarOrdenMostrador: (tokenMostrador: string, nombreCliente: string, items: ItemOrdenEnvio[], notas?: string) =>
    peticionPublica<OrdenMostrador>('/mostrador/orden', {
      method: 'POST',
      body: JSON.stringify({ tokenMostrador, nombreCliente, items, notas }),
    }),
  obtenerOrdenMostrador: (token: string, ordenId: number) =>
    peticionPublica<OrdenMostrador>(`/mostrador/${token}/orden/${ordenId}`),

  solicitarFactura: (datos: { sucursalId: number; cuentaMesaId: number; receptor: any }) =>
    fetch(`${API_BASE}/api/facturacion/solicitar`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(datos),
    }).then((r) => r.json()),
};
